import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";

const emptyDriverForm = () => ({
  name: "",
  code: "",
  description: "",
  unit_type: "number",
  default_value: 0,
  value_type: "periodic",
  category: "general",
  status: "active",
});

function monthRange(start, end) {
  if (!start || !end) return [];
  const out = [];
  let current = new Date(`${start}T00:00:00`);
  const limit = new Date(`${end}T00:00:00`);
  current = new Date(current.getFullYear(), current.getMonth(), 1);

  while (current <= limit) {
    const y = current.getFullYear();
    const m = current.getMonth();
    const monthStart = new Date(y, m, 1);
    out.push(monthStart.toISOString().slice(0, 10));
    current = new Date(y, m + 1, 1);
  }
  return out;
}

export default function DriverLibrary({
  selectedBudget,
  selectedVersionId,
  isAdmin,
}) {
  const { companyId } = useCompany();

  const [sessionUserId, setSessionUserId] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [driverValues, setDriverValues] = useState([]);
  const [form, setForm] = useState(emptyDriverForm());

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  const months = useMemo(() => {
    if (!selectedBudget?.start_date || !selectedBudget?.end_date) return [];
    return monthRange(selectedBudget.start_date, selectedBudget.end_date);
  }, [selectedBudget]);

  const resetMessages = () => {
    setErr("");
    setSuccess("");
  };

  const loadSession = useCallback(async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();

    if (error) throw error;
    setSessionUserId(user?.id || "");
  }, []);

  const loadDrivers = useCallback(async () => {
    if (!companyId) return;

    const { data, error } = await supabase
      .from("planning_drivers")
      .select("*")
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    if (error) throw error;

    setDrivers(data || []);
    if (!selectedDriverId && data?.length) {
      setSelectedDriverId(data[0].id);
    }
  }, [companyId, selectedDriverId]);

  const loadDriverValues = useCallback(async () => {
    if (!companyId || !selectedDriverId || !selectedVersionId) {
      setDriverValues([]);
      return;
    }

    const { data, error } = await supabase
      .from("planning_driver_values")
      .select("*")
      .eq("company_id", companyId)
      .eq("driver_id", selectedDriverId)
      .eq("budget_version_id", selectedVersionId)
      .order("month_start", { ascending: true });

    if (error) throw error;
    setDriverValues(data || []);
  }, [companyId, selectedDriverId, selectedVersionId]);

  const initialize = useCallback(async () => {
    try {
      setLoading(true);
      await loadSession();
      await loadDrivers();
    } catch (e) {
      console.error(e);
      setErr(e.message || "Failed to load drivers.");
    } finally {
      setLoading(false);
    }
  }, [loadDrivers, loadSession]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    loadDriverValues().catch((e) => {
      console.error(e);
      setErr(e.message || "Failed to load driver values.");
    });
  }, [loadDriverValues]);

  const createDriver = async (e) => {
    e.preventDefault();
    resetMessages();

    try {
      setSaving(true);

      const { data, error } = await supabase
        .from("planning_drivers")
        .insert({
          company_id: companyId,
          name: form.name.trim(),
          code: form.code.trim(),
          description: form.description.trim() || null,
          unit_type: form.unit_type,
          default_value: Number(form.default_value || 0),
          value_type: form.value_type,
          category: form.category,
          status: form.status,
          created_by: sessionUserId,
          updated_by: sessionUserId,
        })
        .select()
        .single();

      if (error) throw error;

      if (selectedVersionId) {
        const { error: seedErr } = await supabase.rpc(
          "seed_driver_values_for_budget_version",
          {
            p_driver_id: data.id,
            p_budget_version_id: selectedVersionId,
            p_default_value: Number(form.default_value || 0),
          }
        );

        if (seedErr) throw seedErr;
      }

      setForm(emptyDriverForm());
      setSuccess("Driver created successfully.");
      await loadDrivers();
      setSelectedDriverId(data.id);
    } catch (e2) {
      console.error(e2);
      setErr(e2.message || "Failed to create driver.");
    } finally {
      setSaving(false);
    }
  };

  const seedExistingDriver = async (driverId) => {
    resetMessages();

    try {
      setSaving(true);

      const selected = drivers.find((d) => d.id === driverId);
      const { error } = await supabase.rpc(
        "seed_driver_values_for_budget_version",
        {
          p_driver_id: driverId,
          p_budget_version_id: selectedVersionId,
          p_default_value: Number(selected?.default_value || 0),
        }
      );

      if (error) throw error;

      setSuccess("Driver values seeded successfully.");
      await loadDriverValues();
    } catch (e) {
      console.error(e);
      setErr(e.message || "Failed to seed driver values.");
    } finally {
      setSaving(false);
    }
  };

  const upsertDriverValue = async (monthStart, value) => {
    resetMessages();

    try {
      const { error } = await supabase.rpc("upsert_driver_month_value", {
        p_driver_id: selectedDriverId,
        p_budget_version_id: selectedVersionId,
        p_forecast_version_id: null,
        p_scenario_id: null,
        p_month_start: monthStart,
        p_value: Number(value || 0),
        p_notes: "Updated from driver library UI",
      });

      if (error) throw error;

      await loadDriverValues();
    } catch (e) {
      console.error(e);
      setErr(e.message || "Failed to save driver value.");
    }
  };

  const valueByMonth = useMemo(() => {
    const map = {};
    driverValues.forEach((v) => {
      map[v.month_start] = v;
    });
    return map;
  }, [driverValues]);

  if (loading) {
    return <div className="kp-card">Loading drivers…</div>;
  }

  return (
    <div className="kp-grid-2">
      <div className="kp-card">
        <div className="kp-card-head">
          <h3>Driver Library</h3>
        </div>

        <form className="kp-form" onSubmit={createDriver}>
          <div className="kp-form-grid">
            <div className="kp-field">
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                required
              />
            </div>

            <div className="kp-field">
              <label>Code</label>
              <input
                value={form.code}
                onChange={(e) =>
                  setForm((p) => ({ ...p, code: e.target.value }))
                }
                required
              />
            </div>

            <div className="kp-field">
              <label>Unit Type</label>
              <select
                value={form.unit_type}
                onChange={(e) =>
                  setForm((p) => ({ ...p, unit_type: e.target.value }))
                }
              >
                <option value="number">number</option>
                <option value="count">count</option>
                <option value="currency">currency</option>
                <option value="percentage">percentage</option>
                <option value="days">days</option>
                <option value="units">units</option>
                <option value="headcount">headcount</option>
                <option value="custom">custom</option>
              </select>
            </div>

            <div className="kp-field">
              <label>Default Value</label>
              <input
                type="number"
                value={form.default_value}
                onChange={(e) =>
                  setForm((p) => ({ ...p, default_value: e.target.value }))
                }
              />
            </div>

            <div className="kp-field">
              <label>Value Type</label>
              <select
                value={form.value_type}
                onChange={(e) =>
                  setForm((p) => ({ ...p, value_type: e.target.value }))
                }
              >
                <option value="fixed">fixed</option>
                <option value="growth_rate">growth_rate</option>
                <option value="periodic">periodic</option>
                <option value="derived">derived</option>
              </select>
            </div>

            <div className="kp-field">
              <label>Category</label>
              <select
                value={form.category}
                onChange={(e) =>
                  setForm((p) => ({ ...p, category: e.target.value }))
                }
              >
                <option value="sales">sales</option>
                <option value="marketing">marketing</option>
                <option value="hr">hr</option>
                <option value="operations">operations</option>
                <option value="finance">finance</option>
                <option value="general">general</option>
                <option value="custom">custom</option>
              </select>
            </div>
          </div>

          <div className="kp-field">
            <label>Description</label>
            <textarea
              rows="3"
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
            />
          </div>

          <button className="kp-btn kp-btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Create Driver"}
          </button>
        </form>

        {err && <div className="kp-alert kp-alert-error">{err}</div>}
        {success && <div className="kp-alert kp-alert-success">{success}</div>}

        <div className="kp-list-simple">
          {drivers.map((driver) => (
            <button
              key={driver.id}
              className={`kp-budget-item ${
                selectedDriverId === driver.id ? "selected" : ""
              }`}
              onClick={() => setSelectedDriverId(driver.id)}
            >
              <div className="kp-budget-item-top">
                <strong>{driver.name}</strong>
                <span className="kp-badge status-draft">{driver.code}</span>
              </div>
              <div className="kp-budget-item-sub">
                {driver.unit_type} · default{" "}
                {Number(driver.default_value || 0).toLocaleString()}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="kp-card">
        <div className="kp-card-head">
          <h3>Driver Values by Month</h3>
          {!!selectedDriverId && !!selectedVersionId && (
            <button
              className="kp-btn kp-btn-soft"
              onClick={() => seedExistingDriver(selectedDriverId)}
              disabled={saving}
            >
              Seed Months
            </button>
          )}
        </div>

        {!selectedBudget || !selectedVersionId ? (
          <div className="kp-note">
            Select a budget and version first to manage monthly driver values.
          </div>
        ) : !selectedDriverId ? (
          <div className="kp-note">Select a driver to manage values.</div>
        ) : (
          <div className="kp-month-grid">
            {months.map((monthStart) => (
              <div key={monthStart} className="kp-month-box">
                <label>{monthStart}</label>
                <input
                  type="number"
                  value={valueByMonth[monthStart]?.value ?? ""}
                  onChange={(e) =>
                    upsertDriverValue(monthStart, e.target.value)
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
